import { Image } from '@unpic/react'

interface ShopHeroGalleryProps {
  imageUrl: string | null
  photos: string[] | null
  category: string
}

export function ShopHeroGallery({ imageUrl, photos, category }: ShopHeroGalleryProps) {
  const rawImages = [imageUrl, ...(photos || [])].filter(Boolean) as string[]
  const allImages = Array.from(new Set(rawImages)) // dedup

  if (allImages.length === 0) {
    // Category-themed placeholder
    return (
      <section className="relative w-full max-w-7xl mx-auto h-[400px] md:h-[500px] px-4 md:px-8 py-6">
        <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-sm bg-surface-container-high flex items-center justify-center">
          <span className="text-3xl font-bold text-on-surface-variant opacity-30 uppercase tracking-widest">{category || 'Restaurant'}</span>
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent opacity-50" />
        </div>
      </section>
    )
  }

  // Bento Grid on desktop, horizontal gallery on smaller screens
  if (allImages.length >= 5) {
    return (
      <section className="relative w-full max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="hidden lg:grid grid-cols-4 grid-rows-2 gap-3 h-[500px]">
          <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-2xl">
            <Image src={allImages[0]} layout="fullWidth" alt="Hero main" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent opacity-40 pointer-events-none" />
          </div>
          <div className="col-span-1 row-span-1 relative overflow-hidden">
            <Image src={allImages[1]} layout="fullWidth" alt="Hero secondary" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
          </div>
          <div className="col-span-1 row-span-1 relative overflow-hidden rounded-tr-2xl">
            <Image src={allImages[2]} layout="fullWidth" alt="Hero tertiary" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
          </div>
          <div className="col-span-1 row-span-1 relative overflow-hidden">
            <Image src={allImages[3]} layout="fullWidth" alt="Hero quaternary" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
          </div>
          <div className="col-span-1 row-span-1 relative overflow-hidden rounded-br-2xl">
            <Image src={allImages[4]} layout="fullWidth" alt="Hero quinary" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
          </div>
        </div>
        <div className="lg:hidden flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 no-scrollbar">
          {allImages.slice(0, 5).map((img, index) => (
            <div key={index} className="flex-none w-[85%] md:w-[45%] h-[300px] md:h-[400px] relative rounded-2xl overflow-hidden snap-center shadow-sm">
              <Image src={img} layout="fullWidth" alt={`Gallery photo ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent opacity-40 pointer-events-none" />
            </div>
          ))}
        </div>
      </section>
    )
  }

  // Carousel (2-4 images or ≥ 5 images on mobile)
  if (allImages.length >= 2) {
    return (
      <section className="relative w-full px-4 md:px-8 py-6 max-w-7xl mx-auto overflow-hidden">
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 no-scrollbar">
          {allImages.slice(0, 5).map((img, index) => (
            <div key={index} className="flex-none w-[85%] md:w-[45%] lg:w-[35%] h-[300px] md:h-[400px] relative rounded-2xl overflow-hidden snap-center shadow-sm">
              <Image src={img} layout="fullWidth" alt={`Gallery photo ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent opacity-40 pointer-events-none" />
            </div>
          ))}
        </div>
      </section>
    )
  }

  // Single Hero (1 image)
  return (
    <section className="relative w-full max-w-7xl mx-auto px-4 md:px-8 py-6 h-[400px] md:h-[500px]">
      <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-sm bg-stone-900">
        {/* Blurred background */}
        <div className="absolute inset-0 z-0">
          <Image src={allImages[0]} layout="fullWidth" alt="Background blur" className="w-full h-full object-cover opacity-60 blur-xl scale-110" />
        </div>
        {/* Main image */}
        <Image src={allImages[0]} layout="fullWidth" alt="Hero main" className="relative z-10 w-full h-full object-contain hover:scale-105 transition-transform duration-500 cursor-pointer" />
        <div className="absolute inset-0 z-20 bg-gradient-to-t from-surface via-transparent to-transparent opacity-40 pointer-events-none" />
      </div>
    </section>
  )
}
